import { AD_LAYOUT_STUBS } from "@consts";
import { isEmpty } from "@utils";

const GROUP_COLORS = {
	YELLOW: "yellow",
	GREEN: "green",
	BLUE: "blue",
	PURPLE: "purple",
};

const STATE_KEY = "WFW_NYT_CONNECTIONS";
const CARD_LIMIT = 4;

const loadData = async (axios, config, store) => {
	const response = await axios
		.get(
			`${config.API_SERVICE}/v3/nyt-connections/?date=${getDateQueryParam(
				store
			)}`
		)
		.then((response) => {
			return response;
		})
		.catch((err) => {
			store.commit("error/setStatusCode", err?.response?.status || 500);
		});

	return response
		? {
				todayPuzzle: response?.data?.todayPuzzle,
				pastPuzzles: response?.data?.pastPuzzles,
		  }
		: {};
};

const getDateQueryParam = (store) => {
	const DATE_FORMAT = "sv-SE"; // YYYY-MM-DD
	const offset = store.getters["navigation/isYesterdayConnectionsPage"]
		? -1
		: 0;
	const localeTime = new Date();
	localeTime.setDate(localeTime.getDate() + offset);

	return localeTime.toLocaleDateString(DATE_FORMAT);
};

export default {
	data() {
		// Set up the default reveal state for all 20 cards (16 words, 4 topics)
		const words = [];
		const topics = [];

		for (let i = 0; i < CARD_LIMIT; i++) {
			topics.push(false);
		}

		for (let i = 0; i < Math.pow(CARD_LIMIT, 2); i++) {
			words.push(false);
		}

		return {
			AD_LAYOUT_STUBS,
			todayPuzzle: null,
			pastPuzzles: null,
			serverDateParam: null,
			revealed: {
				words, // For the 16 word cards
				topics, // For the 4 topic cards
			},
		};
	},
	async asyncData({ $axios, $config, store, error }) {
		const connectionsResponse = await loadData($axios, $config, store);
		if (
			connectionsResponse.todayPuzzle &&
			connectionsResponse.pastPuzzles
		) {
			return {
				todayPuzzle: connectionsResponse.todayPuzzle,
				pastPuzzles: connectionsResponse.pastPuzzles,
				serverDateParam: process.server
					? getDateQueryParam(store)
					: null,
			};
		} else {
			error({
				statusCode: 500,
			});
		}
	},
	async mounted() {
		this.loadState();
		if (this.serverDateParam) {
			const clientDateParam = getDateQueryParam(this.$store);
			if (clientDateParam !== this.serverDateParam) {
				const responseData = await loadData(
					this.$axios,
					this.$config,
					this.$store
				);
				this.todayPuzzle = responseData.todayPuzzle;
				this.pastPuzzles = responseData.pastPuzzles;
			}
		}
	},
	computed: {
		allRevealed() {
			const allTopicsRevealed = this.revealed.topics.every((t) => t);
			const allWordsRevealed = this.revealed.words.every((w) => w);

			return allTopicsRevealed && allWordsRevealed;
		},
		subHeading() {
			const firstPart = `${
				this.isYesterdayPage ? "Yesterday's" : "Today's"
			}&nbsp;NYT&nbsp;Connections&nbsp;Hints`;
			const idPart = this.getPuzzleIdDisplay(this.todayPuzzle?.id);
			const secondPart = this.currentDateString;

			if (this.isExtraLargeDevice) {
				return `${firstPart} (${idPart}) - ${secondPart}`;
			}

			return `${firstPart}<br />${idPart} - ${secondPart}`;
		},
		revealedSubheading() {
			const firstPart = "Today’s&nbsp;NYT&nbsp;Connections&nbsp;Answers";
			const idPart = this.getPuzzleIdDisplay(this.todayPuzzle?.id);
			const secondPart = this.currentDateString;

			if (this.isExtraLargeDevice) {
				return `${firstPart} (${idPart}) - ${secondPart}`;
			}

			return `${firstPart}<br />${idPart} - ${secondPart}`;
		},
		currentDateString() {
			return this.humanFriendlyDate("");
		},
		words() {
			return this.todayPuzzle?.startingGroups.flat(1);
		},
		groups() {
			if (!this.todayPuzzle) {
				return [];
			}
			return this.todayPuzzle?.groups.map((groupInfo) => {
				return {
					name: groupInfo.name,
					colorName: this.getGroupColor(groupInfo.level),
					colorHex: this.getColorHex(
						this.getGroupColor(groupInfo.level)
					),
					words: groupInfo.members,
				};
			});
		},
		getPastPuzzles() {
			if (!this.pastPuzzles) {
				return;
			}
			return Object.entries(this.pastPuzzles).map(([key, value]) => {
				return {
					month: this.humanFriendlyDate(`${key}-01`, true),
					puzzles: value.map((puzzle) => {
						return {
							puzzleDate: `${this.humanFriendlyDate(
								puzzle.date
							)} ${this.getPuzzleIdDisplay(puzzle.id)}`,
							puzzleGroups: puzzle.groups.map((groupInfo) => {
								return {
									name: groupInfo.name,
									colorName: this.getGroupColor(
										groupInfo.level
									),
									colorHex: this.getColorHex(
										this.getGroupColor(groupInfo.level)
									),
									words: groupInfo.members,
								};
							}),
						};
					}),
				};
			});
		},
		dateKey() {
			return getDateQueryParam(this.$store);
		},
		metaTitleMonthAndDay() {
			return `${this.$route.meta.title} (${
				this.currentDateString
			}) - ${this.getPuzzleIdDisplay(this.todayPuzzle?.id)}`;
		},
	},
	methods: {
		getGroupColor(groupLevel) {
			switch (groupLevel) {
				case 0:
					return GROUP_COLORS.YELLOW;
				case 1:
					return GROUP_COLORS.GREEN;
				case 2:
					return GROUP_COLORS.BLUE;
				case 3:
					return GROUP_COLORS.PURPLE;
				default:
					return "";
			}
		},
		getWordColor(word) {
			let groupLevel = null;

			for (const group of this.todayPuzzle.groups) {
				if (group.members.includes(word)) {
					groupLevel = group.level;
				}
			}

			return this.getColorHex(this.getGroupColor(groupLevel));
		},
		getColorHex(groupColor) {
			switch (groupColor) {
				case GROUP_COLORS.YELLOW:
					return "#fbe792";
				case GROUP_COLORS.GREEN:
					return "#b8d283";
				case GROUP_COLORS.BLUE:
					return "#c5d2f3";
				case GROUP_COLORS.PURPLE:
					return "#d3add9";
				default:
					return "";
			}
		},
		revealWord(index) {
			this.$set(this.revealed.words, index, true);
			this.saveState();
		},
		revealTopic(index) {
			this.$set(this.revealed.topics, index, true);
			this.saveState();
		},
		revealAll() {
			const words = [];
			const topics = [];

			for (let i = 0; i < CARD_LIMIT; i++) {
				topics.push(true);
			}

			for (let i = 0; i < Math.pow(CARD_LIMIT, 2); i++) {
				words.push(true);
			}

			this.$set(this, "revealed", {
				words,
				topics,
			});
			this.saveState();
		},
		getPuzzleIdDisplay(id) {
			if (!id || isEmpty(id)) {
				return "";
			}
			return `#${id}`;
		},
		humanFriendlyDate(dateAsString, noDay = false) {
			const DATE_FORMAT = "en-US"; // MM-DD-YY
			// Properties of the date object. E.g:
			// Sets the fields of the date(year, month, day, hour, minutes, ...)
			// Sets the timezone of the date. If none mentioned, no offset will be applied to the user agent's date.
			const DATE_OPTIONS = {
				year: "numeric",
				month: "long",
				day: noDay ? undefined : "2-digit",
			};

			// Get the date in the user agent's timezone
			let localeTime;
			if (isEmpty(dateAsString)) {
				localeTime = new Date();
			} else {
				const normalizedDate = dateAsString.includes("T")
					? dateAsString
					: `${dateAsString}T00:00:00`;

				localeTime = new Date(normalizedDate);
			}

			// return the user agent's locale time
			return localeTime.toLocaleDateString(DATE_FORMAT, DATE_OPTIONS);
		},
		saveState() {
			if (process.server) {
				return;
			}

			const wordsCode = this.revealed.words
				.map((w) => (w ? "1" : "0"))
				.join("");
			const topicsCode = this.revealed.topics
				.map((t) => (t ? "1" : "0"))
				.join("");

			const dateCode = getDateQueryParam(this.$store).replace(
				/^\d{2}(\d{2})-(\d{2})-(\d{2})$/,
				"$1$2$3"
			);

			const code = [dateCode, wordsCode, topicsCode].join("_");

			window?.localStorage?.setItem(STATE_KEY, code);
		},
		loadState() {
			if (process.server) {
				return;
			}

			const data = window?.localStorage?.getItem(STATE_KEY);
			if (!data) {
				return;
			}

			const regex = /^(\d{6})_(\d{16})_(\d{4})$/;
			if (!regex.test(data)) {
				// Value is not in a valid syntax. Remove it.
				window?.localStorage?.removeItem(STATE_KEY);
				return;
			}

			const [dateCode, wordsCode, topicsCode] = data.match(regex);
			const todayDateCode = getDateQueryParam(this.$store).replace(
				/^\d{2}(\d{2})-(\d{2})-(\d{2})$/,
				"$1$2$3"
			);

			if (dateCode !== todayDateCode) {
				// Value is not from today. Remove it.
				window?.localStorage?.removeItem(STATE_KEY);
				return;
			}

			const currentAllRevealed = this.revealed.all;

			this.$set(this, "revealed", {
				words: wordsCode.split("").map((c) => c === "1"),
				topics: topicsCode.split("").map((c) => c === "1"),
				all: currentAllRevealed,
			});
		},
	},
};
